import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { useMemo } from 'react';
import { PublishedCustomFields } from '@/components/views/published/PublishedCustomFields';
import type {
  CustomerLookupEntry,
  IdentifiableTItem,
  ItemDetails,
} from '@/components/views/published/types';
import { CUSTOM_FIELD_TYPES, type CustomFieldTypes } from '@/lib/api/types/customField';
import type { ItemAncestor } from '@/lib/api/types/items';
import { TabValue } from '@/lib/types/itemPanel';
import { createReadOnlyCustomFieldsAdapter } from './adapters/custom-fields/createReadOnlyCustomFieldsAdapter';
import { createReadOnlyItemDetailsAdapter } from './adapters/item-details/createReadOnlyItemDetailsAdapter';
import { createReadOnlyLinkedIdeasAdapter } from './adapters/linked-ideas/createReadOnlyLinkedIdeasAdapter';
import { createReadOnlyPlanSchedulingAdapter } from './adapters/plan-scheduling/createReadOnlyPlanSchedulingAdapter';
import { createPublishedSystemFieldsAdapter } from './adapters/system-fields/createPublishedSystemFieldsAdapter';
import { useDependencyTabAdapter } from './hooks/useDependencyTabAdapter';
import { mapItemDetailsToItem } from './hooks/useItemFetchQueryWithPublished';
import { ModularItemPanel } from './index';
import { PublishedDependencies } from './tabs/PublishedDependencies';
import type { TabConfig, ViewPanelConfig } from './types';

const CUSTOM_FIELD_TYPES_SET = new Set<string>(CUSTOM_FIELD_TYPES);

export function toFieldDefs(dtos: CustomFieldDTO[]): Array<{ id: number; type: CustomFieldTypes }> {
  return dtos.flatMap((dto) => {
    const shortType = dto.type.replace('FIELD_TYPE_', '') as CustomFieldTypes;
    if (!CUSTOM_FIELD_TYPES_SET.has(shortType)) return [];
    return [{ id: dto.id, type: shortType }];
  });
}

interface PublishedReadOnlyItemPanelProps {
  itemDetails: ItemDetails | null;
  enrichedItem: IdentifiableTItem | null;
  allItemDetails: Record<string, ItemDetails>;
  enrichedItems: Record<string, IdentifiableTItem>;
  customFieldDefinitions: CustomFieldDTO[];
  hiddenFieldIds?: readonly string[];
  customerLookup?: Record<string, CustomerLookupEntry>;
  expanded: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export const PublishedReadOnlyItemPanel = ({
  itemDetails,
  enrichedItem,
  allItemDetails,
  enrichedItems,
  customFieldDefinitions,
  hiddenFieldIds,
  customerLookup,
  expanded,
  onToggle,
  onClose,
}: PublishedReadOnlyItemPanelProps) => {
  const isOpen = itemDetails !== null && enrichedItem !== null;

  const fieldDefs = useMemo(() => toFieldDefs(customFieldDefinitions), [customFieldDefinitions]);

  const normalizedDefs = useMemo(
    () =>
      customFieldDefinitions.map((dto) => ({
        ...dto,
        type: dto.type.replace('FIELD_TYPE_', ''),
      })) as CustomFieldDTO[],
    [customFieldDefinitions],
  );

  const itemProp = useMemo(
    () => (itemDetails ? mapItemDetailsToItem(itemDetails, fieldDefs) : undefined),
    [itemDetails, fieldDefs],
  );

  const dependencyAdapter = useDependencyTabAdapter(itemProp, { editable: false });

  const viewPanel: ViewPanelConfig = useMemo(() => {
    if (!itemProp) return { adapter: null, isReadOnly: true };
    return {
      isReadOnly: true,
      adapter: createReadOnlyItemDetailsAdapter({
        item: itemProp,
        hiddenFieldIds,
      }),
    };
  }, [itemProp, hiddenFieldIds]);

  const tabs: TabConfig[] = useMemo(() => {
    if (!itemDetails || !itemProp) return [];
    return [
      {
        id: 'system',
        visible: true,
        adapter: createPublishedSystemFieldsAdapter({ itemDetails, hiddenFieldIds }),
      },
      {
        id: 'customFields',
        visible: true,
        adapter: createReadOnlyCustomFieldsAdapter({ item: itemProp }),
        content: (
          <PublishedCustomFields
            itemDetails={itemDetails}
            customFieldDefinitions={normalizedDefs}
            customerLookup={customerLookup}
          />
        ),
      },
      {
        id: 'dependencies',
        visible: true,
        adapter: dependencyAdapter,
        content: (
          <PublishedDependencies
            item={itemProp}
            allItemDetails={allItemDetails}
            enrichedItems={enrichedItems}
          />
        ),
      },
      {
        id: 'linkedIdeas',
        visible: false,
        adapter: createReadOnlyLinkedIdeasAdapter({ item: itemProp }),
      },
      {
        id: 'planScheduling',
        visible: true,
        adapter: createReadOnlyPlanSchedulingAdapter({ item: itemProp, hiddenFieldIds }),
      },
    ];
  }, [
    itemDetails,
    itemProp,
    hiddenFieldIds,
    allItemDetails,
    enrichedItems,
    normalizedDefs,
    customerLookup,
    dependencyAdapter,
  ]);

  const parentHidden = hiddenFieldIds?.includes('parent') ?? false;
  const staticAncestors: ItemAncestor[] | undefined = parentHidden
    ? undefined
    : (itemDetails?.ancestors ?? []).map((a) => ({
        id: a.id,
        depth: a.depth,
        name: a.name,
      }));

  return (
    <ModularItemPanel
      item={itemProp}
      viewPanel={viewPanel}
      tabs={tabs}
      expanded={expanded}
      panelTab={TabValue.SYSTEM}
      open={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      customFieldDefinitions={normalizedDefs}
      staticAncestors={staticAncestors}
      disableAncestorNavigation
    />
  );
};
